import { cn } from "@app/lib/utils";
import { trans } from "@mongez/localization";
import { ChevronDown, ChevronUp } from "lucide-react";
import { SubsidiaryItem } from "./SubsidiaryItem";

interface SubsidiaryCardProps {
  company: {
    name: string;
    capital: string;
    ownership: string;
    activism: string;
    country: string;
    state: string;
    subsidiaries: Array<{
      name: string;
      capital: string;
      ownership: string;
      activism: string;
      country: string;
      state: string;
    }>;
  };
  index: number;
  isOpen: boolean;
  onToggle: () => void;
}

/**
 * Individual subsidiary card component with expandable content
 * Handles both mobile and desktop layouts
 */
export function SubsidiaryCard({
  company,
  index,
  isOpen,
  onToggle,
}: SubsidiaryCardProps) {
  return (
    <div
      className={cn(
        "overflow-hidden rounded-2xl sm:rounded-3xl md:rounded-[32px]",
        "border bg-white px-4 py-2 sm:px-8 sm:py-3 md:px-12 lg:px-14",
      )}
    >
      {/* Header - Always Visible */}
      <button
        onClick={onToggle}
        className={cn(
          "flex w-full items-center justify-between",
          "px-2 py-3 sm:px-4 sm:py-4 md:px-5",
          "transition-all duration-500 ease-in-out",
        )}
      >
        {/* Mobile and Desktop layouts */}
        <div className="w-full">
          {/* Mobile View */}
          <div className="md:hidden">
            <div className="text-start">
              <span
                className={cn("text-sm font-semibold text-marine sm:text-base")}
              >
                {index + 1}. {trans(company.name)}
              </span>
              <div className={cn("mt-2 text-xs text-gray-600")}>
                {trans(
                  "aboutUsPage.leadership.subsidiaries.table.capitalLabel",
                )}
                :{" "}
                {trans(company.capital, {
                  symbol: <span className="font-rubik">^</span>,
                })}
                {trans(
                  "aboutUsPage.leadership.subsidiaries.table.ownershipLabel",
                )}
                : {company.ownership}
              </div>
            </div>
          </div>

          {/* Desktop View */}
          <div
            className={cn(
              "hidden w-full grid-cols-6 items-center md:grid",
              "gap-2 text-start sm:gap-4",
            )}
          >
            <span className={cn("text-sm font-semibold")}>
              {index + 1}. {trans(company.name)}
            </span>
            <span className={cn("text-sm")}>
              {trans(company.capital, {
                symbol: <span className="font-rubik">^</span>,
              })}
            </span>
            <span className={cn("text-sm")}>{company.ownership}</span>
            <span className={cn("text-sm")}>{trans(company.activism)}</span>
            <span className={cn("text-sm")}>{trans(company.country)}</span>
            <span className={cn("text-sm")}>{trans(company.state)}</span>
          </div>
        </div>
        {company.subsidiaries.length > 0 ? (
          isOpen ? (
            <ChevronUp
              className={cn("ml-2 h-4 w-4 flex-shrink-0 sm:ml-4 sm:h-5 sm:w-5")}
            />
          ) : (
            <ChevronDown
              className={cn("ml-2 h-4 w-4 flex-shrink-0 sm:ml-4 sm:h-5 sm:w-5")}
            />
          )
        ) : (
          <span className="ml-2 size-4 flex-shrink-0 sm:ml-4 sm:size-5"></span>
        )}
      </button>

      {/* Animated Drawer Content */}
      {company.subsidiaries.length > 0 && (
        <div
          className={cn(
            "overflow-hidden transition-all duration-500 ease-in-out",
            isOpen ? "max-h-[2000px] opacity-100" : "max-h-0 opacity-0",
          )}
        >
          <div className={cn("space-y-3 border-t py-4 sm:space-y-4 sm:py-6")}>
            {company.subsidiaries.map((subsidiary, subIndex) => (
              <SubsidiaryItem
                key={subIndex}
                subsidiary={subsidiary}
                index={subIndex}
                isParentOpen={isOpen}
              />
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
